import { OTHER_SERIES, type InsightResult } from './insight-compute'
import { insightSegments, insightValues, segmentNamesOf, type InsightRow } from './insight-rows'
import { PREVIOUS_PERIOD, THIS_PERIOD, previousPeriod, scopedRows } from './insight-scope'
import { insightDimensionLabel, type InsightFilter, type InsightSpec } from './insight-spec'

/** One part of a drawn chart: a category on the axis (a bar, a slice, a point, a table row), a
 *  series within it (a stacked segment, a heatmap cell), and the small-multiple panel it is in.
 *  Labels are the RESULT's raw labels (a date bucket's ISO key, not its display form). */
export interface InsightDatum {
  category?: string | undefined
  series?: string | undefined
  facet?: string | undefined
}

/**
 * The rows behind one part of a chart — what a click on a bar, slice, point or cell opens in
 * Library. Computed by the same membership rules `computeInsight` counts with, so the list a
 * reader lands on is exactly the set the number came from: segments, multi-valued labels, a
 * date bucket, and "Other" (the categories or series the chart folded away) included. A datum
 * with nothing named is the whole chart — a figure's rows.
 */
export function insightDrillRows(
  scope: InsightRow[],
  spec: InsightSpec,
  result: InsightResult,
  datum: InsightDatum,
): InsightRow[] {
  const { rows, shown } = panelRows(scope, spec, result, datum)
  if (result.chart === 'kpi') return rows
  const inCategory =
    datum.category === undefined ? rows : categoryRows(rows, spec, shown, datum.category)
  const series = datum.series
  if (
    series === undefined ||
    series === 'value' ||
    series === THIS_PERIOD ||
    series === PREVIOUS_PERIOD
  ) {
    return inCategory
  }
  return seriesRows(inCategory, spec, shown, series)
}

/** The rows a datum starts from — its period's, narrowed to its small-multiple panel — and the
 *  result that panel drew. */
function panelRows(
  scope: InsightRow[],
  spec: InsightSpec,
  result: InsightResult,
  datum: InsightDatum,
): { rows: InsightRow[]; shown: InsightResult } {
  // A period comparison: the series clicked IS the period, and its rows are that period's.
  const previous = previousPeriod(scope, spec)
  const rows =
    previous && datum.series === PREVIOUS_PERIOD ? previous.rows : scopedRows(scope, spec)
  if (datum.facet === undefined || !spec.facet) return { rows, shown: result }
  const facet = spec.facet
  return {
    rows: rows.filter((row) => insightValues(row, facet, spec.bucket).includes(datum.facet!)),
    shown: result.facets.find((panel) => panel.label === datum.facet)?.result ?? result,
  }
}

const segmentsAreCategories = (spec: InsightSpec) =>
  spec.x === null && insightSegments(spec).length > 0

function categoryRows(
  rows: InsightRow[],
  spec: InsightSpec,
  shown: InsightResult,
  category: string,
): InsightRow[] {
  const segments = insightSegments(spec)
  const segmentsAsCategories = segmentsAreCategories(spec)
  const categoriesOf = (row: InsightRow): string[] =>
    segmentsAsCategories
      ? segmentNamesOf(segments, row)
      : spec.x
        ? insightValues(row, spec.x, spec.bucket)
        : []
  const drawn = new Set(
    [...shown.rows.map((row) => row.label), ...shown.points.map((point) => point.label)].filter(
      (label) => label !== OTHER_SERIES,
    ),
  )
  // "Other" on a part-of-a-whole chart is every category the limit cut.
  const folded = category === OTHER_SERIES && !drawn.has(OTHER_SERIES)
  return rows.filter((row) => {
    const values = categoriesOf(row)
    return folded ? values.some((value) => !drawn.has(value)) : values.includes(category)
  })
}

function seriesRows(
  rows: InsightRow[],
  spec: InsightSpec,
  shown: InsightResult,
  series: string,
): InsightRow[] {
  const segments = insightSegments(spec)
  if (!segmentsAreCategories(spec) && segments.length > 0) {
    return rows.filter((row) => segmentNamesOf(segments, row).includes(series))
  }
  if (!spec.series) return rows
  const dimension = spec.series
  const kept = new Set(shown.seriesKeys.filter((key) => key !== OTHER_SERIES))
  return rows.filter((row) => {
    const values = insightValues(row, dimension, spec.bucket)
    return series === OTHER_SERIES
      ? values.some((value) => !kept.has(value))
      : values.includes(series)
  })
}

/** "Hits by outlet — Politico · Negative": what a click-through shows, in words. */
export function describeInsightDatum(spec: InsightSpec, datum: InsightDatum): string {
  const parts = [datum.facet, datum.category, datum.series !== 'value' ? datum.series : undefined]
    .filter((part): part is string => part !== undefined && part !== '')
    .map((part) => (spec.x === 'date' && part === datum.category ? dateWords(part, spec) : part))
  return parts.length > 0 ? `${spec.title} — ${parts.join(' · ')}` : spec.title
}

/** One group a saved chart draws, and whether it grows with the coverage. A `rule` is re-applied
 *  to every refresh, so new articles that match join it; a `fixed` group is the articles (or the
 *  dates) it was saved with, and nothing new ever joins. */
export interface InsightMembership {
  name: string
  kind: 'rule' | 'fixed'
  /** What decides membership, in words: "Outlet is NYT or WaPo", "Mentions layoffs". */
  detail: string
}

const REACH_WORDS = { gt: 'over', gte: 'at least', lt: 'under', lte: 'at most' } as const

function quoteList(values: string[]): string {
  const shown = values.slice(0, 3).map((value) => `“${value}”`)
  const more = values.length > 3 ? ` +${values.length - 3} more` : ''
  return `${shown.join(values.length === 2 ? ' or ' : ', ')}${more}`
}

/** A filter in words — what an analyst would say, not the spec's field names. */
export function describeInsightFilter(filter: InsightFilter): string {
  if (filter.field === 'text') {
    return `${filter.op === 'contains' ? 'Mentions' : 'Doesn’t mention'} ${quoteList(filter.values)}`
  }
  if (filter.field === 'reach') {
    return `Reach ${REACH_WORDS[filter.op]} ${filter.value.toLocaleString('en-US')}`
  }
  const field = insightDimensionLabel(filter.field)
  return `${field} ${filter.op === 'in' ? 'is' : 'isn’t'} ${quoteList(filter.values)}`
}

/**
 * The groups a saved chart draws — its named segments, the narrowing the whole chart applies, its
 * window and any articles it was pinned to — each said as a rule or a fixed set, so nobody is
 * surprised when a group grows after a refresh (or doesn't). `pinned` is the count of articles a
 * dashboard block was saved over from a Library selection (`hitIds`), which lives on the block.
 */
export function describeInsightMembership(
  spec: InsightSpec,
  { pinned = 0 }: { pinned?: number } = {},
): InsightMembership[] {
  const out: InsightMembership[] = []
  if (pinned > 0) {
    out.push({
      name: 'Articles',
      kind: 'fixed',
      detail: `The ${pinned.toLocaleString('en-US')} picked in the Library — new coverage doesn’t join`,
    })
  }
  for (const segment of insightSegments(spec)) {
    out.push({
      name: segment.name,
      kind: 'rule',
      detail: segment.where.map(describeInsightFilter).join(' and '),
    })
  }
  if (spec.where.length > 0) {
    out.push({
      name: 'Whole chart',
      kind: 'rule',
      detail: spec.where.map(describeInsightFilter).join(' and '),
    })
  }
  if (spec.dateFrom || spec.dateTo) {
    const range =
      spec.dateFrom && spec.dateTo
        ? `${spec.dateFrom} to ${spec.dateTo}`
        : spec.dateFrom
          ? `From ${spec.dateFrom}`
          : `Up to ${spec.dateTo}`
    out.push({ name: 'Dates', kind: 'fixed', detail: `${range} — later coverage doesn’t join` })
  } else if (spec.lastDays !== null) {
    out.push({
      name: 'Dates',
      kind: 'rule',
      detail: `The newest ${spec.lastDays} ${spec.lastDays === 1 ? 'day' : 'days'} — moves with each refresh`,
    })
  }
  return out
}

function dateWords(key: string, spec: InsightSpec): string {
  if (/^\d{4}-\d{2}$/.test(key)) return key
  return spec.bucket === 'week' ? `week of ${key}` : key
}
